import { computed, reactive, ref } from 'vue';
import {
  BundleBuilderOptions,
  BundleBuilderState,
  BundleGroupInfo,
  BundleItem,
  CartPayload,
  CheckoutProductSelection,
  ProductComplete,
  ProductSelection,
  ValidationState,
} from '@/features/Product/types/models';
import { AddToCartItem } from '@/features/Cart/types/servicesShopifyCart';
import { useMoneyHelper } from '@/composables/moneyHelpers';
import { useBundlePricing } from '@/features/Product/composables/useBundlePricing';

export const useBundleBuilder = (options: BundleBuilderOptions) => {
  const { bundleDefinition, autoSelectDefaults = true } = options;

  const activeGroupKey = ref<string | null>(null);
  const activeGroupIndex = ref(-1);

  const activeGroup = computed(() => {
    return getBundleGroups().find((group) => group.key === activeGroupKey.value);
  });

  const getGroupKey = (item: BundleItem, index: number): string => {
    return item.groupingName || `group${index}`;
  };

  const getMaxQuantityForProduct = (product: ProductComplete): number => {
    const maxCartQuantity = 5;
    return product.attributes?.maxCartQuantity || maxCartQuantity;
  };
  const setActiveGroup = (groupKey: string, groupIndex: number) => {
    activeGroupKey.value = groupKey;
    activeGroupIndex.value = groupIndex;
  };

  const clearActiveGroup = () => {
    activeGroupKey.value = null;
    activeGroupIndex.value = -1;
  };

  const canAddToGroup = (group: BundleGroupInfo) => {
    if (group.maxSelection && group.totalQuantity >= group.maxSelection) {
      return false;
    }
    const bundleMax = bundleDefinition.maxItems;
    if (bundleMax && totalQuantity.value >= bundleMax) return false;
    return true;
  };

  const getGroupSelections = (group: BundleGroupInfo) => {
    return group.options.filter((option) => option.isSelected && option.quantity > 0);
  };

  const bundleState = reactive<BundleBuilderState>({
    selections: new Map(),
    isInitialised: false,
  });

  const findBundleItem = (groupKey: string): BundleItem | undefined => {
    return bundleDefinition.bundleItems.find(
      (item, index) => getGroupKey(item, index) === groupKey,
    );
  };

  const initialiseBundle = () => {
    if (bundleState.isInitialised) return;

    bundleDefinition.bundleItems.forEach((item, index) => {
      const groupKey = getGroupKey(item, index);

      if (item.itemType === 'fixed') {
        const productMap = new Map<number, ProductSelection>();
        (item.processedProducts || []).forEach((product) => {
          productMap.set(product.attributes.variantId, {
            product,
            quantity: item.quantity || 1,
          });
        });

        bundleState.selections.set(groupKey, {
          products: productMap,
          totalQuantity: (item.processedProducts?.length || 0) * (item.quantity || 1),
        });
      } else if (item.itemType === 'choice_group' && autoSelectDefaults) {
        const minSelection = item.minSelection || 0;
        const productMap = new Map<number, ProductSelection>();
        const defaultProducts = (item.processedProducts || [])
          .filter((product) => !product.attributes.soldOut)
          .slice(0, minSelection);

        defaultProducts.forEach((product) => {
          productMap.set(product.attributes.variantId, {
            product,
            quantity: 1,
          });
        });

        if (productMap.size > 0) {
          bundleState.selections.set(groupKey, {
            products: productMap,
            totalQuantity: defaultProducts.length,
          });
        }
      }
    });
    bundleState.isInitialised = true;
  };

  const addToGroup = (
    groupKey: string,
    product: ProductComplete,
    quantity: number = 1,
  ): boolean => {
    const bundleItem = findBundleItem(groupKey);
    if (!bundleItem) return false;

    let currentSelection = bundleState.selections.get(groupKey);
    if (!currentSelection) {
      currentSelection = {
        products: new Map(),
        totalQuantity: 0,
      };
      bundleState.selections.set(groupKey, currentSelection);
    }

    const existingSelection = currentSelection.products.get(product.attributes.variantId);
    const currentQuantity = existingSelection?.quantity || 0;
    const newQuantity = currentQuantity + quantity;

    const maxForProduct = getMaxQuantityForProduct(product);
    if (newQuantity > maxForProduct) return false;

    if (bundleDefinition.maxItems) {
      const currentBundleTotal = getTotalQuantityAcrossAllGroups();
      const additionalItems = quantity;

      if (currentBundleTotal + additionalItems > bundleDefinition.maxItems) return false;
    }

    if (bundleItem.maxSelection) {
      const currentGroupTotal = currentSelection.totalQuantity;
      const newTotal = currentGroupTotal - currentQuantity + newQuantity;

      if (newTotal > bundleItem.maxSelection) return false;
    }

    if (existingSelection) {
      existingSelection.quantity = newQuantity;
    } else {
      currentSelection.products.set(product.attributes.variantId, {
        product,
        quantity: quantity,
      });
    }

    currentSelection.totalQuantity = Array.from(currentSelection.products.values()).reduce(
      (total, selected) => total + selected.quantity,
      0,
    );

    return true;
  };

  const removeFromGroup = (groupKey: string, variantId: number, quantity: number = 1): boolean => {
    const selection = bundleState.selections.get(groupKey);
    if (!selection) return false;

    const productSelection = selection.products.get(variantId);
    if (!productSelection) return false;

    const newQuantity = productSelection.quantity - quantity;

    if (newQuantity <= 0) {
      selection.products.delete(variantId);
    } else {
      productSelection.quantity = newQuantity;
    }

    selection.totalQuantity = Array.from(selection.products.values()).reduce(
      (total, selected) => total + selected.quantity,
      0,
    );

    if (selection.products.size === 0) {
      bundleState.selections.delete(groupKey);
    }

    return true;
  };

  const setProductQuantity = (
    groupKey: string,
    product: ProductComplete,
    quantity: number,
  ): boolean => {
    if (quantity <= 0) {
      return removeFromGroup(groupKey, product.attributes.variantId, 0);
    }

    const bundleItem = findBundleItem(groupKey);
    if (!bundleItem) return false;

    const maxForProduct = getMaxQuantityForProduct(product);
    if (quantity > maxForProduct) return false;

    let currentSelection = bundleState.selections.get(groupKey);
    if (!currentSelection) {
      currentSelection = {
        products: new Map(),
        totalQuantity: 0,
      };
      bundleState.selections.set(groupKey, currentSelection);
    }

    const existingSelection = currentSelection.products.get(product.attributes.variantId);
    const currentQuantity = existingSelection?.quantity || 0;
    const quantityDiff = quantity - currentQuantity;

    if (bundleDefinition.maxItems) {
      const currentBundleTotal = getTotalQuantityAcrossAllGroups();
      if (currentBundleTotal + quantityDiff > bundleDefinition.maxItems) return false;
    }

    if (bundleItem.maxSelection) {
      const newTotal = currentSelection.totalQuantity + quantityDiff;
      if (newTotal > bundleItem.maxSelection) return false;
    }

    currentSelection.products.set(product.attributes.variantId, {
      product,
      quantity,
    });

    currentSelection.totalQuantity = Array.from(currentSelection.products.values()).reduce(
      (total, selected) => total + selected.quantity,
      0,
    );

    return true;
  };

  const clearGroup = (groupKey: string) => {
    bundleState.selections.delete(groupKey);
  };

  const getTotalQuantityAcrossAllGroups = (): number => {
    let total = 0;
    bundleState.selections.forEach((selection) => {
      total += selection.totalQuantity;
    });
    return total;
  };

  const getProductQuantity = (groupKey: string, variantId: number): number => {
    const selection = bundleState.selections.get(groupKey);
    if (!selection) return 0;
    return selection.products.get(variantId)?.quantity || 0;
  };

  const selectedProducts = computed<ProductSelection[]>(() => {
    const pricing = bundleDefinition.pricingRules;

    if (
      pricing.type === 'first_x_paid' ||
      pricing.type === 'first_x_free' ||
      pricing.type === 'sum'
    ) {
      // Selection order determines which items are paid/free for first_x_paid and first_x_free bundles
      const allItems: { product: ProductComplete; originalPrice: number }[] = [];

      bundleState.selections.forEach((selection) => {
        selection.products.forEach((productSelection) => {
          for (let i = 0; i < productSelection.quantity; i++) {
            allItems.push({
              product: productSelection.product,
              originalPrice: productSelection.product.attributes.price,
            });
          }
        });
      });

      const paidCount = pricing.paidItemsCount || allItems.length;
      const freeCount = pricing.freeItemsCount || 1;

      return allItems.reduce((groupedProducts, current, index) => {
        let price = current.originalPrice;

        if (pricing.type === 'first_x_paid' && index >= paidCount) {
          price = 0;
        }

        if (pricing.type === 'first_x_free' && index < freeCount) {
          price = 0;
        }

        if (pricing.type === 'sum') {
          if (pricing.discountPercentage) {
            price = price * (1 - pricing.discountPercentage / 100);
          } else if (pricing.discountAmount) {
            price = price - pricing.discountAmount / allItems.length;
          }
        }

        const priceInDollars = useMoneyHelper(price).dollarValue;

        const existingEntry = groupedProducts.find(
          (entry) =>
            entry.product.attributes.variantId === current.product.attributes.variantId &&
            entry.price === priceInDollars,
        );

        if (existingEntry) {
          existingEntry.quantity += 1;
        } else {
          groupedProducts.push({
            product: current.product,
            quantity: 1,
            price: priceInDollars,
          });
        }

        return groupedProducts;
      }, [] as ProductSelection[]);
    }

    const products: { product: ProductComplete; quantity: number }[] = [];
    bundleState.selections.forEach((selection) => {
      selection.products.forEach((productSelection) => {
        products.push({
          product: productSelection.product,
          quantity: productSelection.quantity,
        });
      });
    });
    return products;
  });

  const totalQuantity = computed<number>(() => {
    return selectedProducts.value.reduce((total, item) => total + item.quantity, 0);
  });

  const bundlePricing = useBundlePricing(bundleDefinition, selectedProducts);

  const totalPrice = computed(() => bundlePricing.currentPrice.value);

  const fromPrice = computed(() => bundlePricing.fromPrice.value);

  const validationState = computed<ValidationState>(() => {
    const errors: string[] = [];
    const warnings: string[] = [];
    let isValid = true;

    const totalSelected = totalQuantity.value;

    if (bundleDefinition.minItems !== undefined) {
      if (totalSelected < bundleDefinition.minItems) {
        errors.push(
          `Please select at least ${bundleDefinition.minItems} items (currently ${totalSelected})`,
        );
        isValid = false;
      }
    }

    if (bundleDefinition.maxItems !== undefined) {
      if (totalSelected > bundleDefinition.maxItems) {
        errors.push(
          `Maximum ${bundleDefinition.maxItems} items allowed (currently ${totalSelected})`,
        );
        isValid = false;
      }
    }

    bundleDefinition.bundleItems.forEach((item, index) => {
      const groupKey = getGroupKey(item, index);
      const selection = bundleState.selections.get(groupKey);
      const groupQuantity = selection?.totalQuantity || 0;

      if (item.isRequired && groupQuantity === 0) {
        errors.push(`Please select items for ${item.groupingName || 'required group'}`);
        isValid = false;
      }

      if (item.minSelection && groupQuantity < item.minSelection) {
        errors.push(
          `Select at least ${item.minSelection} items for ${item.groupingName || groupKey}`,
        );
        isValid = false;
      }

      if (item.maxSelection && groupQuantity > item.maxSelection) {
        warnings.push(
          `Maximum ${item.maxSelection} items allowed for ${item.groupingName || groupKey}`,
        );
      }
    });

    return { isValid, errors, warnings, totalSelected };
  });

  const isValidBundle = computed<boolean>(() => validationState.value.isValid);

  const createCustomBundleDetails = (
    selectedProducts: ProductSelection[],
  ): CheckoutProductSelection[] => {
    if (!selectedProducts) return [];
    return selectedProducts.reduce((checkoutItems, current) => {
      const { product, quantity, price } = current;
      const { attributes } = product;
      const item_details: CheckoutProductSelection = {
        id: `gid://shopify/ProductVariant/${attributes.variantId}`,
        sku: attributes.sku,
        quantity: quantity || 1,
      };

      // Add the first_x_paid or the sum value so price is correct in checkout
      if (price !== undefined) {
        item_details.price = price;
      }

      checkoutItems.push(item_details);
      return checkoutItems;
    }, []);
  };

  const buildCartObject = (parent_product: ProductComplete = null): CartPayload | null => {
    if (!isValidBundle.value || !parent_product) return null;

    const items: AddToCartItem = {
      id: parent_product.attributes.variantId,
      quantity: 1,
      properties: {
        _custom_bundle_details: JSON.stringify(createCustomBundleDetails(selectedProducts.value)),
        _max_cart_quantity: String(parent_product.attributes.maxCartQuantity ?? 5),
      },
    };

    return {
      items,
    };
  };

  const handleGroupSelection = (product: ProductComplete): boolean => {
    if (!activeGroupKey.value || !activeGroup.value) return false;
    if (activeGroup.value.maxSelection === 1) {
      const currentSelection = getGroupSelections(activeGroup.value)[0];
      if (currentSelection) {
        removeFromGroup(activeGroupKey.value, currentSelection.product.attributes.variantId);
      }
      return addToGroup(activeGroupKey.value, product);
    }
    if (activeGroup.value.totalQuantity < activeGroup.value.maxSelection) {
      return addToGroup(activeGroupKey.value, product);
    }
    return false;
  };

  const getBundleGroups = (): BundleGroupInfo[] => {
    return bundleDefinition.bundleItems.map((item, index) => {
      const groupKey = getGroupKey(item, index);
      const selection = bundleState.selections.get(groupKey);

      return {
        key: groupKey,
        type: item.itemType,
        title: item.groupingName || `Option ${index + 1}`,
        isRequired: item.isRequired,
        minSelection: item.minSelection,
        maxSelection: item.maxSelection,
        totalQuantity: selection?.totalQuantity || 0,
        isValid: !item.isRequired || (selection?.totalQuantity || 0) >= (item.minSelection || 1),
        options: (item.processedProducts || []).map((product) => {
          const productSelection = selection?.products.get(product.attributes.variantId);
          return {
            product,
            quantity: productSelection?.quantity || 0,
            isSelected: !!productSelection,
            isAvailable: !product.attributes.soldOut,
            maxQuantity: getMaxQuantityForProduct(product),
          };
        }),
      };
    });
  };

  initialiseBundle();

  return {
    bundleState,
    selectedProducts,
    totalPrice,
    fromPrice,
    totalQuantity,
    isValidBundle,
    validationState,
    bundleDefinition,
    activeGroup,
    activeGroupKey,
    activeGroupIndex,

    addToGroup,
    removeFromGroup,
    setProductQuantity,
    getProductQuantity,
    clearGroup,
    initialiseBundle,
    buildCartObject,
    getBundleGroups,
    setActiveGroup,
    clearActiveGroup,
    canAddToGroup,
    getGroupSelections,
    handleGroupSelection,
  };
};
